// src/components/forms/order/OrderPaymentInfo.tsx
// Componente para información de pago del pedido

import React, { useCallback } from 'react'
import toast from 'react-hot-toast'
import type { OrderData, PagoParcial, FormaPago, MetodoPago, PlazoPago } from '../../../types/orderFormTypes'
import { Input, Select } from '../../ui'
import { FormField } from '../../ui/Form'
import SectionTitle from '../../ui/SectionTitle'
import { TrashIcon, CreditCardIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline'
import { copyToClipboard } from '../../../utils/clipboard'
import { BOLD_PARTIAL_MIN_AMOUNT_COP } from '../../../constants/boldPayments'
import {
  canAgregarPagoParcial,
  canEditMainPaymentMethod,
  canShowBoldRegenerateButton,
  getMainPaymentMethodEditBlockReason,
  getOrderPagoVerificado,
  isPagoParcialRowLocked,
  isPagoVerificado,
  shouldShowBoldCheckoutLink
} from '../../../utils/orderPaymentEditRules'
import { BoldRegenerateLinkButton } from './BoldRegenerateLinkButton'

const BOLD_CHECKOUT_BASE = 'https://checkout.bold.co/payment/'

function PagoVerificadoBadge({ verificado }: { verificado: boolean }) {
  if (verificado) {
    return (
      <span
        className="inline-flex items-center rounded border border-green-200 bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800"
        aria-label="Pago verificado"
      >
        Verificado
      </span>
    )
  }
  return (
    <span
      className="inline-flex items-center rounded border border-red-200 bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800"
      aria-label="Pago pendiente de verificación"
    >
      Pendiente
    </span>
  )
}

/** En Plan Separe no se permiten abonos con COD, MDM ni Addi en el select */
const METODOS_ABONO_EXCLUIDOS_PLAN_SEPARE = new Set(['cod', 'cmdm', 'addi'])

function getMetodosAbonoSelectOptions(
  metodosPago: MetodoPago[],
  isPlanSepare: boolean,
  pagoMetodoActual: string,
  isPagoRowLocked: boolean
): MetodoPago[] {
  let list = metodosPago.filter((m) => m.value !== 'part' && m.value !== 'plan_separe')
  if (isPlanSepare) {
    list = list.filter((m) => !METODOS_ABONO_EXCLUIDOS_PLAN_SEPARE.has(m.value))
    if (
      isPagoRowLocked &&
      pagoMetodoActual &&
      METODOS_ABONO_EXCLUIDOS_PLAN_SEPARE.has(pagoMetodoActual)
    ) {
      const mantener = metodosPago.find((m) => m.value === pagoMetodoActual)
      if (mantener && !list.some((m) => m.value === mantener.value)) {
        list = [...list, mantener]
      }
    }
  }
  return list
}

interface OrderPaymentInfoProps {
  formData: OrderData
  mode: 'view' | 'edit' | 'create'
  fieldsLocked?: boolean
  errors: { [key: string]: string }
  formasPago: FormaPago[]
  metodosPago: MetodoPago[]
  plazosPago: PlazoPago[]
  pagosParciales: PagoParcial[]
  esValidoPagosParciales: boolean
  totalPedidoPagosParciales: number
  sumaPagosParciales: number
  onInputChange: (section: keyof OrderData, field: string, value: any) => void
  onAgregarPagoParcial: () => void
  onEliminarPagoParcial: (index: number) => void
  onActualizarPagoParcial: (index: number, field: string, value: any) => void
  orderId?: number
  /** Total actual del pedido (líneas + envío) para link Bold */
  getOrderTotalCop: () => number
}

export const OrderPaymentInfo: React.FC<OrderPaymentInfoProps> = ({
  formData,
  mode,
  fieldsLocked = false,
  errors,
  formasPago,
  metodosPago,
  plazosPago,
  pagosParciales,
  esValidoPagosParciales,
  totalPedidoPagosParciales,
  sumaPagosParciales,
  onInputChange,
  onAgregarPagoParcial,
  onEliminarPagoParcial,
  onActualizarPagoParcial,
  orderId,
  getOrderTotalCop
}) => {
  const paymentMethodRaw = formData.extraDetails?.payment_method || formData.payment_method || ''
  const paymentMethod = paymentMethodRaw.toString().trim().toLowerCase()
  const isPartPayment = paymentMethod === 'part' || paymentMethod === 'plan_separe'
  const isPlanSepare = paymentMethod === 'plan_separe'
  const requiresPlazo = paymentMethod === 'cmdm' || paymentMethod === 'cod' || paymentMethod === 'plan_separe'
  const isCodCreateMode = mode === 'create' && paymentMethod === 'cod'
  const pagosExtraDetails = formData.extraDetails?.pagos
  const plazoFromPagos = Array.isArray(pagosExtraDetails) && pagosExtraDetails.length > 0
    ? pagosExtraDetails[0]?.plazo
    : undefined
  const selectedPlazo = formData.extraDetails?.plazo || (plazoFromPagos != null ? String(plazoFromPagos) : '')
  const defaultPlazoOptions: PlazoPago[] = [
    { value: '5', label: '5 días' },
    { value: '15', label: '15 días' },
    { value: '30', label: '30 días' }
  ]
  const basePlazoOptions: PlazoPago[] = plazosPago.length > 0 ? plazosPago : defaultPlazoOptions
  const plazoOptions: PlazoPago[] = paymentMethod === 'cmdm'
    ? basePlazoOptions.filter((plazo) => plazo.value !== '60')
    : basePlazoOptions
  const requiresTransactionId = (method: string) => {
    return method === 'wompi' || method === 'mpago' || method === 'bacs' || method === 'addi'
  }
  /** En fila parcial, Addi solo exige ID en Plan Separe (no en Pagos parciales). */
  const requiresTransactionIdPartialRow = (method: string) => {
    const m = (method || '').trim().toLowerCase()
    if (m === 'wompi' || m === 'mpago' || m === 'bacs') return true
    if (m === 'addi' && isPlanSepare) return true
    return false
  }
  // Métodos que no requieren ID de transacción
  const noRequiresTransactionId = (method: string) => {
    return method === 'cod' || method === 'cash' || method === 'cmdm' || method === 'dataf'
  }

  const canEditPaymentMethod = canEditMainPaymentMethod(mode, formData.status, {
    paymentMethod,
    pagosParciales
  })
  const mainPaymentMethodBlockReason = getMainPaymentMethodEditBlockReason(mode, formData.status, {
    paymentMethod,
    pagosParciales
  })
  const orderPagoVerificado = getOrderPagoVerificado(formData)
  const puedeAgregarAbono = canAgregarPagoParcial(mode, formData.status, paymentMethod)
  const isMainPaymentFieldLocked = mode === 'view' || fieldsLocked
  const showMainBoldRegenerate =
    mode === 'edit' &&
    orderId != null &&
    paymentMethod === 'bold' &&
    canShowBoldRegenerateButton(mode, orderPagoVerificado, formData.status, paymentMethod)

  const handleMainBoldRegenerated = useCallback(
    (transactionId: string) => {
      onInputChange('extraDetails', 'transaction_id', transactionId)
    },
    [onInputChange]
  )

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-4 mt-2">
      <SectionTitle icon={CreditCardIcon}>Información de Pago</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Forma de Pago */}
        <FormField
          label="Forma de Pago"
          required
          error={errors.forma_pago}
        >
          <Select
            value={formData.extraDetails?.forma_pago === '7' ? '6' : formData.extraDetails?.forma_pago}
            onChange={(e) => onInputChange('extraDetails', 'forma_pago', e.target.value)}
            disabled={true}
            options={[
            
              ...formasPago.map((forma) => ({
                value: forma.value,
                label: forma.label
              }))
            ]}
            placeholder="Seleccione"
            error={!!errors.forma_pago}
            errorMessage={errors.forma_pago}
            mode={mode}
          />
        </FormField>

        {/* Método de Pago */}
        <FormField
          label="Método de Pago"
          required
          error={errors.payment_method}
        >
          <Select
            value={paymentMethodRaw}
            onChange={(e) => onInputChange('extraDetails', 'payment_method', e.target.value)}
            disabled={!canEditPaymentMethod}
            title={mainPaymentMethodBlockReason}
            options={[
              ...metodosPago.map((metodo) => ({
                value: metodo.value,
                label: metodo.label
              }))
            ]}
            placeholder="Seleccione"
            error={!!errors.payment_method}
            errorMessage={errors.payment_method}
            mode={mode}
          />
        </FormField>

        {requiresPlazo && (
          <FormField
            label="Plazo"
            required
            error={errors.plazo}
          >
            <Select
              value={selectedPlazo}
              onChange={(e) => onInputChange('extraDetails', 'plazo', e.target.value)}
              disabled={mode === 'view' || isCodCreateMode || isMainPaymentFieldLocked}
              options={[
                { value: '', label: 'Seleccione' },
                ...plazoOptions.map((plazo: PlazoPago) => ({
                  value: plazo.value,
                  label: plazo.label
                }))
              ]}
              placeholder={isCodCreateMode ? '15 días (fijo para contraentrega)' : 'Seleccione plazo'}
              error={!!errors.plazo}
              errorMessage={errors.plazo}
              mode={mode}
            />
          </FormField>
        )}

        {/* ID Transacción (solo si no es pago parcial; para Bold se bloquea y se genera automáticamente) */}
        {!isPartPayment && (paymentMethod === 'bold' || !noRequiresTransactionId(paymentMethod)) && (
          <div className="flex flex-col gap-0.5">
            <FormField
              label="ID Transacción"
              required={(mode === 'create' || mode === 'edit') && requiresTransactionId(paymentMethod)}
              error={errors.transaction_id}
            >
              <Input
                type="text"
                value={formData.extraDetails?.transaction_id || ''}
                onChange={(e) => onInputChange('extraDetails', 'transaction_id', e.target.value)}
                readOnly={mode === 'view' || isMainPaymentFieldLocked || noRequiresTransactionId(paymentMethod) || paymentMethod === 'bold'}
                disabled={isMainPaymentFieldLocked || noRequiresTransactionId(paymentMethod) || paymentMethod === 'bold'}
                placeholder={paymentMethod === 'bold' ? 'Se generará automáticamente para Bold' : noRequiresTransactionId(paymentMethod) ? 'No aplica para este método de pago' : 'ID de transacción'}
                error={!!errors.transaction_id}
                errorMessage={errors.transaction_id}
                mode={mode}
              />
            </FormField>
            {paymentMethod === 'bold' && (
              <div className="flex flex-col gap-1">
                {showMainBoldRegenerate && (
                  <BoldRegenerateLinkButton
                    orderId={orderId!}
                    variant="main"
                    getTotalAmount={getOrderTotalCop}
                    storeName={String(formData.store_name || formData.extraDetails?.store_name || '')}
                    status={String(formData.status || 'verify_transaction')}
                    paymentMethod="bold"
                    disabled={orderPagoVerificado}
                    onRegenerated={(txId) => handleMainBoldRegenerated(txId)}
                  />
                )}
                {shouldShowBoldCheckoutLink(formData.status) &&
                  (formData.extraDetails?.transaction_id || '').trim() && (
                  <div className="flex items-center gap-1">
                    <a
                      href={`${BOLD_CHECKOUT_BASE}${(formData.extraDetails?.transaction_id || '').trim()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-gray-400 hover:text-blue-600 hover:underline truncate flex-1 min-w-0"
                    >
                      {`${BOLD_CHECKOUT_BASE}${(formData.extraDetails?.transaction_id || '').trim()}`}
                    </a>
                    <button
                      type="button"
                      onClick={async () => {
                        const url = `${BOLD_CHECKOUT_BASE}${(formData.extraDetails?.transaction_id || '').trim()}`
                        const ok = await copyToClipboard(url)
                        if (ok) toast.success('Link copiado', { icon: '📋', position: 'top-center' })
                      }}
                      className="flex-shrink-0 p-0.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50/50 rounded transition-colors"
                      title="Copiar link"
                      aria-label="Copiar link"
                    >
                      <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Campos de Pagos Parciales / Abonos */}
      {isPartPayment && (
        <div className="mt-6">
          <div className={`border rounded-lg p-4 ${
            esValidoPagosParciales
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-4">
                <h4 className={`text-md font-semibold ${
                  esValidoPagosParciales
                    ? 'text-green-800' 
                    : 'text-red-800'
                }`}>{isPlanSepare ? 'Abonos' : 'Pagos Parciales'}</h4>
                <div className="text-sm text-gray-600">
                  Ingresado: <span className="font-bold">${sumaPagosParciales.toLocaleString('es-CO')}</span>
                  <span className="mx-2">|</span>
                  Total: <span className="font-bold">${totalPedidoPagosParciales.toLocaleString('es-CO')}</span>
                  <span className={`ml-2 ${esValidoPagosParciales ? 'text-green-600' : 'text-red-600'}`}>
                    {esValidoPagosParciales ? '✓' : '⚠'}
                  </span>
                </div>
              </div>
            </div>
            {puedeAgregarAbono && (
              <div className="flex justify-end mb-4">
                <button
                  type="button"
                  onClick={onAgregarPagoParcial}
                  className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                >
                  {isPlanSepare ? '+ Agregar abono' : '+ Agregar Pago'}
                </button>
              </div>
            )}
              
            {pagosParciales.length === 0 ? (
              <p className="text-gray-600 text-sm">
                {isPlanSepare ? 'No hay abonos agregados' : 'No hay pagos parciales agregados'}
              </p>
            ) : (
              <div className="space-y-3">
                {pagosParciales.map((pago, index) => {
                  const rowLocked = isPagoParcialRowLocked(mode, formData.status, pago, paymentMethod)
                  const valorFila = Math.round(Number(pago.valor) || 0)
                  const esFilaBold = String(pago.metodo_pago || '').trim().toLowerCase() === 'bold'
                  const boldBajoMinimo =
                    mode !== 'view' &&
                    !rowLocked &&
                    esFilaBold &&
                    valorFila > 0 &&
                    valorFila < BOLD_PARTIAL_MIN_AMOUNT_COP
                  const errValorParcial = errors[`pago_parcial_valor_${index}`]
                  const valorParcialEnError = Boolean(errValorParcial) || boldBajoMinimo
                  const mensajeValorParcial =
                    errValorParcial ||
                    (boldBajoMinimo
                      ? `El monto es menor al mínimo ($${BOLD_PARTIAL_MIN_AMOUNT_COP.toLocaleString('es-CO')}) para Bold.`
                      : '')
                  const pagoVerificado = isPagoVerificado(pago.pago_verificado)
                  return (
                  <div key={index} className="relative bg-white border border-gray-200 rounded-md p-3">
                    {(mode === 'edit' || mode === 'view') && (
                      <div className="absolute top-3 right-3 z-10">
                        <PagoVerificadoBadge verificado={pagoVerificado} />
                      </div>
                    )}
                    <div className={`grid grid-cols-1 gap-3 ${isPlanSepare ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
                      {/* Método de Pago */}
                      <FormField
                        label="Método de Pago"
                        required
                        error={errors[`pago_parcial_metodo_${index}`]}
                      >
                        <Select
                          value={pago.metodo_pago}
                          onChange={(e) => onActualizarPagoParcial(index, 'metodo_pago', e.target.value)}
                          disabled={mode === 'view' || rowLocked}
                          options={[
                            { value: '', label: 'Seleccione' },
                            ...getMetodosAbonoSelectOptions(
                              metodosPago,
                              isPlanSepare,
                              pago.metodo_pago,
                              rowLocked
                            ).map((metodo) => ({
                              value: metodo.value,
                              label: metodo.label
                            }))
                          ]}
                          placeholder="Seleccione"
                          error={!!errors[`pago_parcial_metodo_${index}`]}
                          errorMessage={errors[`pago_parcial_metodo_${index}`]}
                          mode={mode}
                        />
                      </FormField>
                      
                      {/* ID Transacción */}
                      <div className="flex flex-col gap-0.5">
                        <FormField
                          label="ID Transacción"
                          required={(mode === 'create' || mode === 'edit') && requiresTransactionIdPartialRow(pago.metodo_pago)}
                          error={errors[`pago_parcial_transaction_${index}`]}
                        >
                          <Input
                            type="text"
                            value={pago.id_transaccion || ''}
                            onChange={(e) => onActualizarPagoParcial(index, 'id_transaccion', e.target.value)}
                            readOnly={
                              mode === 'view' ||
                              rowLocked ||
                              pago.metodo_pago === 'cod' ||
                              pago.metodo_pago === 'cash' ||
                              pago.metodo_pago === 'dataf' ||
                              pago.metodo_pago === 'bold'
                            }
                            disabled={
                              rowLocked ||
                              pago.metodo_pago === 'cod' ||
                              pago.metodo_pago === 'cash' ||
                              pago.metodo_pago === 'dataf' ||
                              pago.metodo_pago === 'bold'
                            }
                            placeholder={
                              pago.metodo_pago === 'bold'
                                ? 'Se generará al guardar el pedido'
                                : pago.metodo_pago === 'cod' 
                                ? 'No aplica para contra entrega' 
                                : pago.metodo_pago === 'cash'
                                ? 'No aplica para pago en efectivo'
                                : pago.metodo_pago === 'dataf'
                                ? 'No aplica para datafono'
                                : 'ID de transacción'
                            }
                            error={!!errors[`pago_parcial_transaction_${index}`]}
                            errorMessage={errors[`pago_parcial_transaction_${index}`]}
                            mode={mode}
                          />
                        </FormField>
                        {pago.metodo_pago === 'bold' &&
                          shouldShowBoldCheckoutLink(formData.status) &&
                              (pago.id_transaccion || '').trim() && (
                              <div className="flex items-center gap-1">
                                <a
                                  href={`${BOLD_CHECKOUT_BASE}${(pago.id_transaccion || '').trim()}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[11px] text-gray-400 hover:text-blue-600 hover:underline truncate flex-1 min-w-0"
                                >
                                  {`${BOLD_CHECKOUT_BASE}${(pago.id_transaccion || '').trim()}`}
                                </a>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const url = `${BOLD_CHECKOUT_BASE}${(pago.id_transaccion || '').trim()}`
                                    const ok = await copyToClipboard(url)
                                    if (ok) toast.success('Link copiado', { icon: '📋', position: 'top-center' })
                                  }}
                                  className="flex-shrink-0 p-0.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50/50 rounded transition-colors"
                                  title="Copiar link"
                                  aria-label="Copiar link"
                                >
                                  <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                                </button>
                              </div>
                        )}
                      </div>
                      
                      <div
                        className={
                          valorParcialEnError
                            ? 'rounded-md border border-red-300 bg-red-50/60 p-2 pb-7 -m-0.5 relative'
                            : ''
                        }
                      >
                      <FormField
                        label={
                          String(pago.metodo_pago || '').trim().toLowerCase() === 'bold' ? (
                            <>
                              <span>Valor</span>
                              <span className="text-xs font-normal text-gray-500">
                                (Monto mínimo ${BOLD_PARTIAL_MIN_AMOUNT_COP.toLocaleString('es-CO')})
                              </span>
                            </>
                          ) : (
                            'Valor'
                          )
                        }
                        required
                        error={valorParcialEnError ? mensajeValorParcial : undefined}
                      >
                        <Input
                          type="text"
                          value={pago.valor ? pago.valor.toLocaleString('es-CO') : ''}
                          onChange={(e) => {
                            if (rowLocked) return
                            const valorSinFormato = e.target.value.replace(/[^\d]/g, '')
                            const valorNumerico = valorSinFormato ? Math.round(parseInt(valorSinFormato)) : 0
                            onActualizarPagoParcial(index, 'valor', valorNumerico)
                          }}
                          readOnly={mode === 'view' || rowLocked}
                          disabled={rowLocked}
                          placeholder="0"
                          error={valorParcialEnError}
                          errorMessage={valorParcialEnError ? mensajeValorParcial : undefined}
                          mode={mode}
                        />
                      </FormField>
                      </div>

                      {/* ID de abono (solo Plan Separe; asignado por backend) */}
                      {isPlanSepare && (
                        <FormField label="ID de abono">
                          <Input
                            type="text"
                            value={(pago.dcto || '').trim() || 'Pendiente'}
                            readOnly
                            disabled
                            mode={mode}
                            aria-label="ID de abono"
                          />
                        </FormField>
                      )}

                      {/* Acciones: Nuevo link Bold + Eliminar (alineado con inputs) */}
                      <FormField
                        label={<span className="invisible select-none" aria-hidden="true">Acciones</span>}
                        className="mb-2"
                      >
                        <div className="flex items-center gap-2 flex-wrap min-h-[38px]">
                          {mode === 'edit' &&
                            orderId != null &&
                            esFilaBold &&
                            canShowBoldRegenerateButton(mode, pagoVerificado, formData.status, paymentMethod) && (
                            <BoldRegenerateLinkButton
                              orderId={orderId}
                              variant="partial"
                              rowIndex={index}
                              getTotalAmount={() => Math.round(Number(pagosParciales[index]?.valor) || 0)}
                              getPagosFromForm={() =>
                                pagosParciales.map((p) => ({ ...p })) as Array<Record<string, unknown>>
                              }
                              storeName={String(
                                formData.store_name || formData.extraDetails?.store_name || ''
                              )}
                              status={String(formData.status || 'verify_transaction')}
                              disabled={pagoVerificado}
                              onRegenerated={(txId) =>
                                onActualizarPagoParcial(index, 'id_transaccion', txId)
                              }
                            />
                          )}
                          {mode !== 'view' && !rowLocked && (
                            <button
                              type="button"
                              onClick={() => onEliminarPagoParcial(index)}
                              className="px-2 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-xs flex items-center gap-1.5"
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                              Eliminar
                            </button>
                          )}
                        </div>
                      </FormField>
                    </div>
                  </div>
                  )
                })}
              </div>
            )}
            
            {errors.pagos && (
              <span className="text-red-500 text-sm mt-2 block">{errors.pagos}</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
